import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { LogoBase, type LogoBaseProps } from "./logo-base";
export interface LogoPaths {
  outlined: string;
  filled: string;
}
export interface LogoConfig {
  paths: LogoPaths;
  /**
   * viewBox for this logo (required — logos have varying aspect ratios)
   */
  viewBox: string;
}
export interface LogoComponentProps extends Omit<LogoBaseProps, "children"> {
  variant?: "outlined" | "filled";
}

/**
 * Type for logo components created by the factory
 */
export type LogoComponent = React.ForwardRefExoticComponent<LogoComponentProps & React.RefAttributes<SVGSVGElement>>;

/**
 * Factory function to create logo components with minimal boilerplate.
 *
 * @param displayName - The display name for the component (used in React DevTools)
 * @param config - Object containing paths and viewBox
 * @returns A forwardRef logo component
 *
 * @example
 * export const VaWaterfront = createLogo("VaWaterfront", {
 *   paths: { outlined: "M647...", filled: "M647..." },
 *   viewBox: "0 0 140 144"
 * });
 */
export function createLogo(displayName: string, config: LogoConfig) {
  const {
    paths,
    viewBox
  } = config;
  const Logo = React.forwardRef<SVGSVGElement, LogoComponentProps>((t0, ref) => {
    const $ = _c(9);
    let props;
    let t1;
    if ($[0] !== t0) {
      ({
        variant: t1,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = props;
      $[2] = t1;
    } else {
      props = $[1];
      t1 = $[2];
    }
    const variant = t1 === undefined ? "outlined" : t1;
    const t2 = paths[variant];
    let t3;
    if ($[3] !== t2) {
      t3 = <path d={t2} />;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== props || $[6] !== ref || $[7] !== t3) {
      t4 = <LogoBase ref={ref} viewBox={viewBox} {...props}>{t3}</LogoBase>;
      $[5] = props;
      $[6] = ref;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  });
  Logo.displayName = displayName;
  return Logo;
}